import { FC } from 'react'
import { ArrowRight } from 'lucide-react'
import CardInfoButton from './CardInfoButton'
import type { CardMeta } from './types'

const nearMissMeta: CardMeta = {
  description: 'Recent near miss reports submitted across the fleet.',
  endpoint: 'GET /qhse/near-miss-reports',
  logic: 'Displays recent 5 near misses sorted by dateOfOccurrence descending.',
}

const statusBadge = (status?: string | null) => {
  const s = (status || '').toUpperCase()
  if (s === 'CLOSE') return 'bg-emerald-50 text-emerald-600'
  if (s === 'SUBMIT') return 'bg-amber-50 text-amber-600'
  return 'bg-slate-50 text-slate-600'
}

const fmtDate = (d: string | null | undefined) => {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

interface Props {
  nearMisses: any[]
  onViewAll: () => void
}

const NearMissSection: FC<Props> = ({ nearMisses, onViewAll }) => {
  const recent = nearMisses.slice(0, 5)

  return (
    <div className='flex flex-col flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5'>
      <div className='flex items-center justify-between mb-3'>
        <div className='flex items-center gap-2'>
          <h6 className='text-sm font-bold text-slate-900'>Open Near Misses</h6>
          <CardInfoButton meta={nearMissMeta} />
        </div>
        <button onClick={onViewAll} className='text-[10px] font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1 transition-colors'>
          View all <ArrowRight className='w-3 h-3' />
        </button>
      </div>
      <div className='overflow-x-auto max-h-[280px] overflow-y-auto'>
        <table className='w-full text-[11px]'>
          <thead>
            <tr className='text-slate-400 text-[10px] uppercase tracking-wider'>
              <th className='text-left font-semibold py-2 pr-2'>Date</th>
              <th className='text-left font-semibold py-2 px-2'>Vessel</th>
              <th className='text-left font-semibold py-2 px-2'>Description</th>
              <th className='text-right font-semibold py-2 pl-2'>Status</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {recent.length === 0 ? (
              <tr>
                <td colSpan={4} className='text-center text-slate-400 py-8 text-xs'>No near miss reports found</td>
              </tr>
            ) : (
              recent.map((nm, i) => (
                <tr key={nm.id ?? i} className='hover:bg-slate-50 transition-colors'>
                  <td className='py-2.5 pr-2 text-slate-700'>{fmtDate(nm.dateOfOccurrence || nm.dateIssued)}</td>
                  <td className='py-2.5 px-2 font-semibold text-slate-800'>{nm.vesselName || '—'}</td>
                  <td className='py-2.5 px-2 text-slate-500 max-w-[220px] truncate'>
                    {nm.descriptionOfOccurrence || nm.occurrenceTypes?.join(', ') || '—'}
                  </td>
                  <td className='py-2.5 pl-2 text-right'>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${statusBadge(nm.status)}`}>
                      {nm.status || 'SAVE'}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default NearMissSection
